import { useMemo, useState, useEffect } from 'preact/hooks';
import { useTranslation, useTranslationList, parseRichText } from '../i18n';
import { useRelativeTime } from '../i18n/useRelativeTime';
import { useFetch } from '../hooks/useFetch';
import { api } from '../api/client';
import { useLocale } from '../i18n';
import { PLATFORM_VERSION, FFXIV_PATCH, LINKS } from '../brand';
import {
    SectionHeading,
    Eyebrow,
    ArrowLink,
    ErrorTile,
    Skeleton,
    PageBody,
} from '../primitives';
import { EmporiumNav } from '../components/EmporiumNav';
import { Colophon } from '../components/Colophon';
import { LatestArticle } from '../components/LatestArticle';
import { PublicVisibilityBadge } from '../primitives/PublicVisibilityBadge';

/**
 * Landing page - the root of the unified SPA. Five vertical sections under the platform nav,
 * with the catalog teaser pulling the freshest mirrored works and an editorial latest-article
 * slot. Ported from the v1 SPA landing page, with the cross-subdomain helpers
 * replaced by same-origin paths.
 *
 *   1. Hero - floating Loporrit (md+) + Instrument Serif italic quote + lede
 *   2. StartHereCtaRow - 4-card learn-then-do funnel (Articles → Pack → Studio → Emporium)
 *   3. CatalogTeaser - table of newest mirrored works + Open Emporium CTA
 *   4. LatestArticle - featured article slot
 *   5. Colophon - 4-column footer with copyright + FFXIV disclaimer
 */
export function LandingPage() {
    const { apiSet } = useLocale();
    const rel = useRelativeTime();

    // Landing only needs the catalog headline numbers + the 8 newest
    // works. The backend sorts by -LastChangedAt and emits
    // X-Total-Count, so one tiny page is enough to populate the hero
    // teaser; iterating the full catalog (~500 rows over multiple
    // pages) on a landing route would burn perf for no UX win.
    const summaries = useFetch(() => api.works.list({ pageSize: 8 }, apiSet), [apiSet]);
    const sources = useFetch(() => api.providers.list(), []);

    const workCount = summaries.status === 'success' ? summaries.data.totalCount : '…';
    // Surface the curated source roster (enabled crawlers) rather than
    // re-deriving from a partial catalog page. Counts match the chip
    // list in the catalog sidebar.
    const sourceCount =
        sources.status === 'success'
            ? sources.data.filter((s) => s.isEnabled).length
            : '…';

    const latest = useMemo(() => {
        if (summaries.status !== 'success') return [];
        return summaries.data.items;
    }, [summaries]);

    const emporiumHref = '/emporium';
    const articlesHref = '/articles';
    const studioHref = '/studio';
    const packHref = LINKS.github.pack;

    return (
        <div class="flex min-h-full flex-col">
            <EmporiumNav current={null} />

            <main class="flex-1">
                <Hero />

                <StartHereCtaRow
                    sourceCount={sourceCount}
                    workCount={workCount}
                    articlesHref={articlesHref}
                    emporiumHref={emporiumHref}
                    studioHref={studioHref}
                    packHref={packHref}
                />

                <CatalogTeaser
                    state={summaries.status}
                    error={summaries.status === 'error' ? summaries.error : null}
                    latest={latest}
                    emporiumHref={emporiumHref}
                    workCount={workCount}
                    rel={rel}
                />

                <LatestArticle articlesHref={articlesHref} />
            </main>

            <Colophon emporiumHref={emporiumHref} studioHref={studioHref} packHref={packHref} />
        </div>
    );
}

/**
 * Hero - hoisted to module scope so it is NOT redefined on every LandingPage
 * render. As a nested closure it got a fresh function identity each parent
 * render, so Preact unmounted + remounted it, resetting useRotatingHeroLine's
 * index state (snapping the quote back to the motto) and replaying the
 * lopo-fade animation on every re-render. At module scope its state persists
 * across parent renders. It pulls its own translator rather than closing over
 * LandingPage's.
 */
function Hero() {
    const t = useTranslation();
    const motto = t('landing.heroQuote');
    const rotation = useTranslationList('landing.heroQuotes');
    const line = useRotatingHeroLine(motto, rotation);
    const quoteSegments = parseRichText(line);
    return (
        <header class="relative overflow-visible border-b border-rule">
            <PageBody rhythm="tight">
                <div class="relative">
                    {/* Floating Loporrit - absolute + height:0 so it doesn't push following sections. */}
                    {/* Served via the media pipeline: brand asset seeded with a known
                        id (loporrit-mascot) at boot, then the `mascot` recipe re-encodes
                        the 516×1024 PNG source to a 600px-wide WebP at q85 - ~10× wire
                        savings vs. the raw PNG, retina-safe for the clamp(180px,22vw,280px)
                        CSS width. index.html preloads the same URL for the LCP win. */}
                    <div class="pointer-events-none absolute left-0 top-0 hidden h-0 w-[clamp(180px,22vw,280px)] md:block">
                        <img
                            src="/media/loporrit-mascot/mascot"
                            alt=""
                            class="block w-full drop-shadow-[0_24px_40px_rgba(0,0,0,0.55)] [animation:lopo-bob_6s_ease-in-out_infinite] [transform-origin:50%_80%]"
                        />
                    </div>

                    <div class="md:pl-[calc(clamp(180px,22vw,280px)+clamp(24px,4vw,56px))]">
                        <div class="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                            <span>{t('landing.issue', { version: PLATFORM_VERSION, patch: FFXIV_PATCH })}</span>
                            <span class="text-fg-faint">·</span>
                            <span>{t('landing.hello')}</span>
                        </div>

                        <h1 class="mt-4 font-display text-[clamp(36px,4.6vw,64px)] italic font-normal leading-[1.05] tracking-tight text-fg-primary">
                            <span key={line} class="block text-balance [animation:lopo-fade_700ms_ease-out]">
                                {'“'}
                                {quoteSegments.map((seg, i) =>
                                    seg.highlighted
                                        ? <em key={i} class="italic text-accent">{seg.text}</em>
                                        : <span key={i}>{seg.text}</span>,
                                )}
                                {'”'}
                            </span>
                            <span class="mt-3 block font-mono text-[clamp(15px,1.1vw,18px)] font-light not-italic uppercase tracking-[0.04em] text-fg-muted">
                                {t('landing.heroAttrib')}
                            </span>
                        </h1>

                        <p class="mt-6 max-w-[52ch] font-display text-[clamp(17px,1.4vw,21px)] font-light leading-[1.45] text-fg-primary">
                            {t('landing.heroLede')}{' '}
                            <span class="text-fg-muted">{t('landing.heroLedeMuted')}</span>
                        </p>
                    </div>
                </div>
            </PageBody>
        </header>
    );
}

/**
 * The hero quote rotates once a minute through the classic motto plus the
 * witty `landing.heroQuotes` set. The first render is deterministic - it
 * shows the motto at index 0 so there is no flash of a random quote before
 * the client settles. On mount the client jumps to a random line so each
 * visit opens on a fresh one, then advances every 60 seconds. When the locale
 * has no `heroQuotes` (or it is empty) the motto simply stays put.
 *
 * This SPA does not prerender. Crawler-facing and no-JS social copy comes
 * from the OpenGraph default card injected into the shell head
 * (Koan.Web.OpenGraph, configured under Koan:Web:OpenGraph), not from any
 * server-rendered body HTML.
 */
function useRotatingHeroLine(motto: string, quotes: readonly string[]): string {
    const lines = useMemo(() => [motto, ...quotes], [motto, quotes]);
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (lines.length <= 1) return;
        setIndex(Math.floor(Math.random() * lines.length));
        const id = window.setInterval(() => {
            setIndex((i) => (i + 1) % lines.length);
        }, 60_000);
        return () => window.clearInterval(id);
    }, [lines.length]);
    return lines[index % lines.length] ?? motto;
}

function StartHereCtaRow({
    sourceCount,
    workCount,
    articlesHref,
    emporiumHref,
    studioHref,
    packHref,
}: {
    sourceCount: number | string;
    workCount: number | string;
    articlesHref: string;
    emporiumHref: string;
    studioHref: string;
    packHref: string;
}) {
    const t = useTranslation();
    return (
        <section class="border-b border-rule bg-surface-raised">
            <div class="mx-auto max-w-[1680px] px-[clamp(20px,3.5vw,56px)] py-[clamp(48px,6vw,80px)] md:pl-[calc(clamp(20px,3.5vw,56px)+clamp(180px,22vw,280px)+clamp(24px,4vw,56px))]">
                <div class="mb-8 flex flex-wrap items-baseline justify-between gap-4">
                    <Eyebrow>{t('landing.ctaStartHere')}</Eyebrow>
                    <div class="flex flex-wrap items-baseline gap-3 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                        <RichInline value={t('landing.ctaStatsPrints', { n: workCount })} />
                        <span class="text-fg-faint">·</span>
                        <RichInline value={t('landing.ctaStatsSources', { n: sourceCount })} />
                        <span class="text-fg-faint">·</span>
                        <RichInline value={t('landing.ctaStatsLicense')} />
                    </div>
                </div>

                {/* Card order Articles → Pack → Studio → Emporium maps the platform's
                    learn-then-do funnel: read an article to understand what ReShade is, install
                    the Pack to get a curated setup, use Studio to keep it fresh, browse the
                    Emporium for more. */}
                <div class="grid grid-cols-1 gap-0 overflow-hidden rounded-md border border-rule bg-surface-raised sm:grid-cols-2 sm:divide-x sm:divide-rule lg:grid-cols-4">
                    <CtaCard
                        title={t('landing.ctaArticlesTitle')}
                        body={t('landing.ctaArticlesBody')}
                        tail={t('landing.ctaArticlesTail')}
                        href={articlesHref}
                    />
                    <CtaCard
                        title={t('landing.ctaPackTitle')}
                        body={t('landing.ctaPackBody')}
                        tail={t('landing.ctaPackTail', { version: `v${PLATFORM_VERSION}` })}
                        href={packHref}
                        external
                    />
                    <CtaCard
                        title={t('landing.ctaStudioTitle')}
                        body={t('landing.ctaStudioBody')}
                        tail={t('landing.ctaStudioTail')}
                        href={studioHref}
                    />
                    <CtaCard
                        title={t('landing.ctaEmporiumTitle')}
                        body={t('landing.ctaEmporiumBody')}
                        tail={t('landing.ctaEmporiumTail')}
                        href={emporiumHref}
                    />
                </div>
            </div>
        </section>
    );
}

function CtaCard({
    title,
    body,
    tail,
    href,
    external,
}: {
    title: string;
    body: string;
    tail: string;
    href: string;
    external?: boolean;
}) {
    return (
        <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noreferrer noopener' : undefined}
            class="group flex flex-col gap-3 px-[clamp(20px,3vw,40px)] py-6 transition-colors hover:bg-accent-glow"
        >
            <SectionHeading title={title} size="md" as="h3" />
            <p class="max-w-[36ch] text-sm text-fg-muted">{body}</p>
            <span class="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-secondary transition-colors group-hover:text-accent">
                {tail}
                <span class="transition-transform group-hover:translate-x-1">→</span>
            </span>
        </a>
    );
}

function CatalogTeaser({
    state,
    error,
    latest,
    emporiumHref,
    workCount,
    rel,
}: {
    state: 'idle' | 'loading' | 'success' | 'error';
    error: Error | null;
    latest: Array<{
        publiclyListed?: boolean;
        id: string;
        name?: string | null;
        author?: string | null;
        source?: string | null;
        primary: { scoutSlug?: string | null };
        sightings: { lastChangedAt?: string | null; scoutSlugs: string[] };
        upstream?: { updatedAt?: string | null };
        lastSeenAt?: string | null;
        install: { kind: string };
        installable: boolean;
    }>;
    emporiumHref: string;
    workCount: number | string;
    rel: (when: Date | string | number) => string;
}) {
    const t = useTranslation();
    const buildWorkHref = (id: string) => `/work/${encodeURIComponent(id)}`;

    return (
        <section class="border-b border-rule">
            <PageBody rhythm="section">
                <div class="mb-6 flex flex-wrap items-end justify-between gap-4">
                    <SectionHeading
                        eyebrow={t('landing.catalogTeaserEyebrow')}
                        title={t('landing.catalogTeaserTitle')}
                        size="md"
                        as="h2"
                    />
                    <ArrowLink href={emporiumHref} variant="primary">
                        {t('landing.catalogTeaserCta').replace(/\s*→\s*$/, '')}
                    </ArrowLink>
                </div>

                {state === 'loading' && <Skeleton.Grid count={6} mode="row" />}

                {state === 'error' && error && <ErrorTile title={error.message} />}

                {state === 'success' && (
                    <>
                        <div class="overflow-hidden rounded-md border border-rule bg-surface-raised">
                            <div class="hidden grid-cols-[28px_1fr_160px_100px_80px] gap-4 border-b border-rule-strong px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-fg-muted md:grid">
                                <span></span>
                                <span>{t('catalogRow.colName')}</span>
                                <span>{t('catalogRow.colAuthor')}</span>
                                <span>{t('catalogRow.colInstall')}</span>
                                <span class="text-right">{t('catalogRow.colLastUpdate')}</span>
                            </div>
                            {latest.map((pkg) => {
                                const canInstall = pkg.installable && (pkg.install.kind === 'Mirror' || pkg.install.kind === 'Upstream');
                                return (
                                    <a
                                        key={pkg.id}
                                        href={buildWorkHref(pkg.id)}
                                        class="group grid grid-cols-[28px_1fr_100px] items-center gap-4 border-b border-rule px-4 py-2.5 transition-colors last:border-b-0 hover:bg-accent-glow md:grid-cols-[28px_1fr_160px_100px_80px]"
                                    >
                                        <span
                                            class="h-2.5 w-2.5 rounded-full"
                                            style={{ background: `var(--color-source-${pkg.source ?? 'direct'}, var(--color-fg-faint))` }}
                                        />
                                        <div class="min-w-0">
                                            <span class="block truncate font-display text-[17px] italic font-normal text-fg-primary transition-colors group-hover:text-accent">
                                                {pkg.name ?? t('catalogRow.unnamed')}
                                            </span>
                                            <PublicVisibilityBadge publiclyListed={pkg.publiclyListed} />
                                        </div>
                                        <span class="hidden truncate text-sm text-fg-muted md:block">
                                            {pkg.author ?? '-'}
                                        </span>
                                        <RowActionPill canInstall={canInstall} label={canInstall ? t('row.actionInstall') : t('row.actionVisit')} />
                                        <span class="hidden text-right font-mono text-[11px] text-fg-faint md:block">
                                            {rel(
                                                pkg.upstream?.updatedAt
                                                    ?? pkg.sightings.lastChangedAt
                                                    ?? pkg.lastSeenAt
                                                    ?? Date.now(),
                                            )}
                                        </span>
                                    </a>
                                );
                            })}
                        </div>

                        <div class="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-5">
                            <a
                                href={emporiumHref}
                                class="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2 font-sans text-[13px] font-medium text-surface-base transition-colors hover:bg-fg-primary"
                            >
                                {t('landing.catalogOpenCta')} <span>→</span>
                            </a>
                            <span class="font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                                {t('landing.catalogEntriesNote', { n: workCount })}
                            </span>
                        </div>
                    </>
                )}
            </PageBody>
        </section>
    );
}

function RowActionPill({ canInstall, label }: { canInstall: boolean; label: string }) {
    if (canInstall) {
        return (
            <span class="inline-flex items-center justify-center gap-1 rounded-full border border-accent bg-accent px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-surface-base transition-colors group-hover:border-fg-primary group-hover:bg-fg-primary">
                <DownloadIcon />
                {label}
            </span>
        );
    }
    return (
        <span class="inline-flex items-center justify-center gap-1 rounded-full border border-rule-strong px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-secondary transition-colors group-hover:border-fg-muted group-hover:text-fg-primary">
            <ExternalIcon />
            {label}
        </span>
    );
}

function DownloadIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}

/**
 * Inline renderer for translated strings that include `<b>...</b>` markup (stats formatters
 * bold numeric values).
 */
function RichInline({ value }: { value: string }) {
    const re = /<b>(.*?)<\/b>/g;
    const segments: { text: string; bold: boolean }[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(value)) !== null) {
        if (match.index > lastIndex) segments.push({ text: value.slice(lastIndex, match.index), bold: false });
        segments.push({ text: match[1], bold: true });
        lastIndex = re.lastIndex;
    }
    if (lastIndex < value.length) segments.push({ text: value.slice(lastIndex), bold: false });

    return (
        <span>
            {segments.map((s, i) =>
                s.bold
                    ? <b key={i} class="font-medium text-fg-primary">{s.text}</b>
                    : <span key={i}>{s.text}</span>,
            )}
        </span>
    );
}
